import { useEffect, useRef, useState } from 'react';
import { Check, ImagePlus, LoaderCircle, Plus, RotateCcw, X } from 'lucide-react';
import { getDeviceId } from '../../utils/deviceId';
import { getErrorMessage } from '../../utils/errorMessage';
import { MediaPreviewButton } from '../components/MediaPreview';
import './CreationReferences.css';

export const MAX_DRAFT_IMAGES = 4;
const TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const savedReferences = items => (Array.isArray(items) ? items : []).filter(item => item && typeof item.id === 'string'
  && typeof item.name === 'string' && typeof item.url === 'string' && /^https?:\/\//.test(item.url)
  && typeof item.token === 'string' && item.token.length <= 10000 && TYPES.includes(item.mime)).slice(0, 12)
  .map(({ id, name, url, token, mime, size }) => ({ id, name, url, token, mime, size }));

async function uploadImage(file, signal, preview) {
  if (preview) {
    const url = await new Promise((resolve, reject) => {
      const reader = new FileReader(); reader.onload = () => resolve(reader.result); reader.onerror = () => reject(new Error('无法读取图片，请重新选择。')); reader.readAsDataURL(file);
    });
    return { url, mime: file.type, size: file.size, name: file.name };
  }
  const token = localStorage.getItem('token');
  const response = await fetch('/api/creation/references', { method: 'POST', body: file, signal,
    headers: { 'Content-Type': file.type, 'X-File-Name': encodeURIComponent(file.name), 'X-Xiaoying-Device-ID': getDeviceId(), ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || '图片上传失败，请重试。');
  if (!savedReferences([body]).length) throw new Error('未收到图片上传结果，请重试。');
  return body;
}

export function useCreationReferences(initial, { preview, used = 0 }) {
  const [items, setItems] = useState(() => savedReferences(initial).map(item => ({ ...item, status: 'ready' })));
  const [error, setError] = useState('');
  const current = useRef(items);
  const alive = useRef(true);
  const pumping = useRef(false);
  const controllers = useRef(new Map());
  const commit = change => { current.current = change(current.current); if (alive.current) setItems(current.current); };
  const revoke = item => { if (item?.localUrl) URL.revokeObjectURL(item.localUrl); };
  useEffect(() => {
    alive.current = true;
    const activeControllers = controllers.current;
    return () => { alive.current = false; queueMicrotask(() => {
      if (!alive.current) { activeControllers.forEach(controller => controller.abort()); current.current.forEach(revoke); }
    }); };
  }, []);
  const pump = async () => {
    if (pumping.current) return;
    pumping.current = true;
    try {
      while (alive.current) {
        const item = current.current.find(ref => ref.status === 'queued');
        if (!item) break;
        const controller = new AbortController(); controllers.current.set(item.id, controller);
        const timer = setTimeout(() => controller.abort(), 50000);
        commit(list => list.map(ref => ref.id === item.id ? { ...ref, status: 'uploading' } : ref));
        try {
          const uploaded = await uploadImage(item.file, controller.signal, preview);
          commit(list => list.map(ref => ref.id === item.id ? { ...uploaded, id: uploaded.id || item.id, status: 'ready' } : ref));
          revoke(item);
        } catch (failure) {
          commit(list => list.map(ref => ref.id === item.id ? { ...ref, status: 'failed', error: controller.signal.aborted ? '上传超时，请重试' : getErrorMessage(failure, '上传失败，请重试。') } : ref));
        } finally { clearTimeout(timer); controllers.current.delete(item.id); }
      }
    } finally { pumping.current = false; }
  };
  const add = files => {
    const problems = [];
    const added = [];
    for (const file of Array.from(files || [])) {
      if (!TYPES.includes(file.type)) { problems.push('支持 JPG、PNG 和 WebP 图片'); continue; }
      if (!file.size || file.size > 10 * 1024 * 1024) { problems.push('单张图片需在 10 MB 以内'); continue; }
      if (current.current.length + added.length >= MAX_DRAFT_IMAGES) { problems.push('每次最多添加 4 张图片'); break; }
      if (used + current.current.length + added.length >= 12) { problems.push('一次对话最多参考 12 张图片'); break; }
      const duplicate = [...current.current, ...added].some(item => item.file && item.file.name === file.name && item.file.size === file.size && item.file.lastModified === file.lastModified);
      if (duplicate) continue;
      added.push({ id: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`, name: file.name, mime: file.type, size: file.size, localUrl: URL.createObjectURL(file), file, status: 'queued' });
    }
    setError([...new Set(problems)].join('；'));
    commit(list => [...list, ...added]);
    void pump();
  };
  const clear = () => { current.current.forEach(item => { controllers.current.get(item.id)?.abort(); revoke(item); }); commit(() => []); setError(''); };
  return { items, error, add, clear,
    blocked: items.some(item => item.status !== 'ready'),
    serializable: () => current.current.filter(item => item.status === 'ready').map(({ status, ...item }) => item),
    remove: id => { const item = current.current.find(ref => ref.id === id); controllers.current.get(id)?.abort(); revoke(item); commit(list => list.filter(ref => ref.id !== id)); setError(''); },
    retry: id => { commit(list => list.map(ref => ref.id === id ? { ...ref, status: 'queued', error: '' } : ref)); void pump(); },
  };
}

export function ReferenceUploadButton({ onFiles, disabled, onBeforeSelect }) {
  const input = useRef(null);
  return <div className="creation-reference-upload">
    <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden aria-label="选择参考图片"
      onChange={event => { onFiles(event.target.files); event.target.value = ''; }} />
    <button type="button" aria-label="上传参考图片" title="上传参考图片 · 每次最多 4 张，单张 10 MB" disabled={disabled}
      onClick={() => { if (onBeforeSelect?.() === false) return; input.current?.click(); }}><Plus size={19} /><span>参考图片</span></button>
  </div>;
}

export function ReferenceDrafts({ attachments }) {
  return <>
    {attachments.items.length > 0 && <div className="creation-references" aria-label="待发送的参考图片">
      {attachments.items.map(item => <div className={`creation-reference is-${item.status}`} key={item.id}>
        <MediaPreviewButton src={item.localUrl || item.url} title={item.name} className="creation-reference__preview"><img src={item.localUrl || item.url} alt={item.name} /></MediaPreviewButton>
        <div className="creation-reference__info"><span title={item.name}>{item.name}</span>
          <small role="status">{item.status === 'ready' ? <><Check size={11} />已添加</> : item.status === 'failed' ? '上传失败' : <><LoaderCircle size={11} />{item.status === 'queued' ? '等待上传' : '上传中…'}</>}</small>
        </div>
        <button type="button" className="creation-reference__remove" aria-label={`移除 ${item.name}`} onClick={() => attachments.remove(item.id)}><X size={12} /></button>
        {item.status === 'failed' && <div className="creation-reference__failure"><span title={item.error}>{item.error}</span><button type="button" onClick={() => attachments.retry(item.id)}><RotateCcw size={12} />重试</button></div>}
      </div>)}
    </div>}
    {attachments.error && <p className="creation-references__error" role="alert"><ImagePlus size={13} />{attachments.error}</p>}
  </>;
}

export function MessageReferences({ references }) {
  if (!references?.length) return null;
  return <div className="creation-message-references" aria-label="消息中的参考图片">{references.map(ref => <MediaPreviewButton key={ref.id} src={ref.url} title={ref.name}>
    <img src={ref.url} alt={ref.name} loading="lazy" /><span>{ref.name}</span>
  </MediaPreviewButton>)}</div>;
}
